import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { EntitySlotUtils, EntityInstance, EntitySlotSelectionMap } from '../types/PostGenerationTypes';
import { useEntityRegistry } from '../../template-editor/hooks/useEntityRegistry';

export function useCreatePostPrefill(
  entitySlots: EntitySlotSelectionMap,
  updateEntitySelection: (slotKey: string, instance: EntityInstance | null) => void
) {
  const [searchParams] = useSearchParams();
  const prefillEntityType = searchParams.get('prefillEntityType');
  const prefillEntityId = searchParams.get('prefillEntityId');
  
  const { registry, getAdapter } = useEntityRegistry();
  const [hasPrefilledEntity, setHasPrefilledEntity] = React.useState(false);

  React.useEffect(() => {
    if (!prefillEntityType || !prefillEntityId) return;
    if (hasPrefilledEntity) return;

    const slotsForType = EntitySlotUtils.getSlotsByEntityType(entitySlots, prefillEntityType);
    if (!slotsForType.length) return;

    const targetSlot = slotsForType.find(slot => !slot.selectedInstance) || slotsForType[0];
    if (!targetSlot) return;

    const adapter = getAdapter(prefillEntityType);
    if (!adapter || !adapter.findById) return;

    let cancelled = false;

    (async () => {
      try {
        const fullData = await adapter.findById(prefillEntityId);
        if (!fullData || cancelled) return;

        const instance: EntityInstance = {
          id: prefillEntityId,
          label: registry.generateLabel(prefillEntityType, fullData),
          data: fullData,
          type: prefillEntityType
        };

        updateEntitySelection(targetSlot.slotKey, instance);
        setHasPrefilledEntity(true);
      } catch (error) {
        console.error('Failed to prefill entity slot', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [prefillEntityType, prefillEntityId, entitySlots, getAdapter, registry, updateEntitySelection, hasPrefilledEntity]);
}
